import AlerterView from 'chairisher/view/alerter';
import ObjectUtils from 'chairisher/util/object';

import {
    reportTotalMismatchDuringFinalization,
    trackPaymentComplete,
    trackPaymentRequestAuthorized,
    trackPaymentRequestAvailable,
    trackPaymentRequestInitiated,
    trackSubmitError,
} from 'chairisher/analytics/checkout';
import { isAuthenticated } from 'chairisher/context/auth';
import { getGenericPaymentError } from 'chairisher/context/error';
import { getPaymentProcessorCodes, getPaymentRequestWalletNames } from 'chairisher/context/payment';
import { getRedactedAddressPlaceholder, getRedactedPhonePlaceholder } from 'chairisher/context/purchase';
import { logError } from 'chairisher/util/analytics';
import { postJSON } from 'chairisher/util/fetch';
import { handleAsyncError } from 'chairisher/util/logging';
import { isTradeInsuranceAvailable } from 'chairisher/view/helper/checkout';

const { doesObjHaveErrors } = ObjectUtils;

const MAX_SHIPPING_OPTION_DESCRIPTION_LENGTH = 200;
const PAYMENT_REQUEST_COUNTRY = 'US';
const PAYMENT_REQUEST_CURRENCY = 'usd';

const PaymentRequestEvents = Object.freeze({
    CANCEL: 'cancel',
    CLICK: 'click',
    SHIPPING_ADDRESS_CHANGE: 'shippingaddresschange',
    SHIPPING_OPTION_CHANGE: 'shippingoptionchange',
    TOKEN: 'token',
});

/**
 * @see https://stripe.com/docs/js/appendix/update_details
 */
const UPDATE_DETAILS_STATUS = Object.freeze({
    SUCCESS: 'success',
    FAIL: 'fail',
    INVALID_SHIPPING_ADDRESS: 'invalid_shipping_address',
});

function handleError(e) {
    logError('payment request checkout error', e);
}

/**
 * @param options {object}
 * @param options.label {string} The line item label E.G. "$27.69"
 * @param options.value {string} The line item value E.G. "27.69"
 * @returns {object} A receipt item that can be rendered by the payment request UI.
 * @see https://stripe.com/docs/js/appendix/payment_item_object
 */
function makePaymentItem({ label, value }) {
    return {
        amount: Math.round(parseFloat(value) * 100), // Stripe expects a cent amount integer.
        label,
    };
}

/**
 * @param options {Object}
 * @param options.value {string} Shipping partner value
 * @param options.display {string} Shipping partner display string
 * @param options.detail {string} Detailed information about shipping partner; newlines are acceptable.
 * @param options.price {number} The cost of shipping with the partner.
 * @returns {Object} An option that can be rendered by the payment request UI.
 * @see https://stripe.com/docs/js/appendix/shipping_option
 */
function makeShippingOption({ value, display, detail, price }) {
    return {
        id: value,
        label: display,
        detail: detail?.length < MAX_SHIPPING_OPTION_DESCRIPTION_LENGTH ? detail : '',
        amount: price, // This doesn't need to be converted to cents for some reason.
    };
}

/**
 * Returns a purchase object that can be used to bootstrap an express checkout.
 * This will be merged with a saved shipping address from the OS when the UI is opened.
 * Note: this only accepts a single product since the UI doesn't support different shipping options per product.
 *
 * @param {Number} initialTotal The cart's initial total - this will be overwritten after the first request completes.
 * @param {Number} productId
 * @param {Number} quantity
 * @param {Number} productPrice
 * @param {Number} numProducts
 * @returns {Object} A purchase object that can be used to bootstrap an express checkout
 */
export function makePurchaseDataForExpressCheckout({ initialTotal, productId, quantity, productPrice, numProducts }) {
    const product = {
        id: { value: productId },
        price: { value: productPrice },
        quantity: { value: quantity },
    };
    return {
        meta: { purchase_total: { value: initialTotal } },
        products: numProducts === 1 ? [product] : [],
        purchase_details: {
            receipt_summary: {
                value: [
                    {
                        type: 'total',
                        value: initialTotal,
                    },
                    {
                        // Fixes an issue with Google Pay where receipt will not be shown if it's initially empty.
                        // This value will be overwritten on the initial purchase response.
                        type: '',
                        value: '0',
                        label: 'Details',
                    },
                ],
            },
        },
    };
}

/**
 * Provides data and callbacks to the browser's payment request UI, e.g. Apple Pay / Google Pay
 */
class PaymentRequestCheckout {
    /**
     * @param {object} options
     * @param {string} options.buttonHeight
     * @param {string} options.buttonSelector
     * @param {string} options.buttonType
     * @param {TrackingExpressCheckoutOrigin=} options.expressCheckoutOrigin
     * @param {function} options.getPurchaseData
     * @param {boolean=} options.isExpressCheckout
     * @param {object} options.stripe
     * @see https://stripe.com/docs/stripe-js/elements/payment-request-button
     */
    constructor({
        buttonHeight,
        buttonSelector,
        buttonType,
        expressCheckoutOrigin = null,
        getPurchaseData,
        isExpressCheckout = false,
        purchaseUrl,
        stripe,
    }) {
        this.buttonHeight = buttonHeight;
        this.buttonSelector = buttonSelector;
        this.buttonType = buttonType;
        this.expressCheckoutOrigin = expressCheckoutOrigin;
        this.getPurchaseData = getPurchaseData;
        this.isExpressCheckout = isExpressCheckout;
        this.purchaseUrl = purchaseUrl;
        this.stripe = stripe;

        /**
         * The name of the available wallet, if any.
         * @type {null|string}
         */
        this.availableWalletName = null;

        /**
         * A reference to the last purchase data used by this payment request.
         * Make sure not to mutate this as it's sometimes shared with our checkout UI.
         *
         * @type {null|object}
         */
        this.purchaseData = this.getPurchaseData();

        /**
         * Whether a placeholder was sent for address information.
         *
         * @type {boolean}
         */
        this.wasAddressRedacted = false;
    }

    /**
     * @returns {Object} The first product data object from purchase response (the UI can only handle 1 product).
     */
    #getProductData() {
        return this.purchaseData.products[0];
    }

    /**
     * The line items to display in the payment request UI.
     *
     * @returns {Array.<Object>}
     */
    #getPaymentItems() {
        return this.#getReceiptSummary()
            .filter(({ type }) => type !== 'total')
            .map(makePaymentItem);
    }

    #getReceiptSummary() {
        return this.purchaseData.purchase_details.receipt_summary.value;
    }

    /**
     * Returns the selectable shipping options.
     * The payment request UI doesn't support different options per product, so we limit to single product carts.
     *
     * @returns {array.<Object>} A list of options that can be rendered by the payment request UI.
     */
    #getShippingOptions() {
        const productData = this.#getProductData();
        const shippingChoices = productData.shipping_partner_code?.choices ?? [];
        return shippingChoices.map((choice) => {
            let detailParts = choice.additional_info_strings || [];

            // Avoid mutating purchase data.
            detailParts = [...detailParts];

            if (choice.estimated_delivery_display) {
                detailParts.push(choice.estimated_delivery_display);
            }

            return makeShippingOption({
                value: choice.value,
                display: choice.display,
                detail: detailParts.join('\n'),
                price: choice.price * 100, // Stripe expects a cent amount integer
            });
        });
    }

    /**
     * @returns {string} The receipt's 'total' line item.
     */
    #getTotal() {
        return this.#getReceiptSummary().find(({ type }) => type === 'total').value;
    }

    /**
     * The total amount to display in the payment request UI.
     *
     * @returns {Object}
     */
    #getTotalPaymentItem() {
        return makePaymentItem({
            label: 'Total',
            value: this.#getTotal(),
        });
    }

    /**
     * @returns {Promise} A promise that resolves once the instance is ready to use.
     */
    async initialize() {
        const walletNames = getPaymentRequestWalletNames();
        const paymentRequestOptions = {
            country: PAYMENT_REQUEST_COUNTRY,
            currency: PAYMENT_REQUEST_CURRENCY,
            displayItems: this.#getPaymentItems(),
            requestPayerEmail: true,
            requestPayerName: true,
            requestPayerPhone: this.isExpressCheckout,
            requestShipping: this.isExpressCheckout,
            total: this.#getTotalPaymentItem(),
        };
        if (this.expressCheckoutOrigin && this.isEligibleForExpressCheckout()) {
            paymentRequestOptions.shippingOptions = this.#getShippingOptions();
        }

        // See https://stripe.com/docs/js/payment_request/create
        const paymentRequest = this.stripe.paymentRequest(paymentRequestOptions);

        // See https://stripe.com/docs/js/elements_object/create_element?type=paymentRequestButton
        const paymentRequestButton = this.stripe.elements().create('paymentRequestButton', {
            paymentRequest,
            style: {
                paymentRequestButton: {
                    height: this.buttonHeight,
                    type: this.buttonType,
                },
            },
        });

        paymentRequestButton.on(PaymentRequestEvents.CLICK, () => {
            // Always set a new purchase data on click to ensure payment request UI matches current cart state.
            this.purchaseData = this.getPurchaseData();

            trackPaymentRequestInitiated(this.availableWalletName, this.isExpressCheckout);
            paymentRequest.update({
                total: this.#getTotalPaymentItem(),
                displayItems: this.#getPaymentItems(),
            });
        });

        if (this.isExpressCheckout) {
            paymentRequest.on(PaymentRequestEvents.CANCEL, () => {
                // Reinitialize payment request button if UI is closed.
                // This fixes an issue with Google Pay where subsequent opens had different behavior than initial open.
                // Specifically, the address change event wasn't emitted, resulting in an incomplete purchase request.
                paymentRequestButton.destroy();
                this.initialize();
            });
        }

        // Bind the checkout callback functions - these connect the payment request UI with our backend.
        paymentRequest.on(PaymentRequestEvents.SHIPPING_ADDRESS_CHANGE, this.#onShippingAddressChange.bind(this));
        paymentRequest.on(PaymentRequestEvents.SHIPPING_OPTION_CHANGE, this.#onShippingOptionChange.bind(this));
        paymentRequest.on(PaymentRequestEvents.TOKEN, this.#onToken.bind(this));

        const result = await paymentRequest.canMakePayment();
        if (result?.applePay) {
            this.availableWalletName = walletNames.APPLE_PAY;
        } else if (result?.googlePay) {
            this.availableWalletName = walletNames.GOOGLE_PAY;
        }
        if (this.availableWalletName) {
            paymentRequestButton.mount(this.buttonSelector);
        }
    }

    /**
     * @returns {boolean}
     */
    isAvailable() {
        return !!this.availableWalletName;
    }

    isApplePayAvailable() {
        const walletNames = getPaymentRequestWalletNames();
        return this.availableWalletName === walletNames.APPLE_PAY;
    }

    isGooglePayAvailable() {
        const walletNames = getPaymentRequestWalletNames();
        return this.availableWalletName === walletNames.GOOGLE_PAY;
    }

    /**
     * @returns {boolean}
     */
    isEligibleForExpressCheckout() {
        return this.purchaseData.products && this.purchaseData.products.length === 1;
    }

    /**
     * Note: Apple/Google strip non-essential pieces of shipping data before payment.
     *
     * @see https://stripe.com/docs/js/payment_request/events/on_shipping_address_change
     * @see https://stripe.com/docs/js/appendix/shipping_address
     */
    async #onShippingAddressChange({ shippingAddress, updateWith }) {
        try {
            await this.#submit({ shippingAddress });
            updateWith({
                status: doesObjHaveErrors(this.purchaseData.shipto_address)
                    ? UPDATE_DETAILS_STATUS.INVALID_SHIPPING_ADDRESS
                    : UPDATE_DETAILS_STATUS.SUCCESS,
                total: this.#getTotalPaymentItem(),
                displayItems: this.#getPaymentItems(),
                shippingOptions: this.#getShippingOptions(),
            });
        } catch (e) {
            updateWith({ status: UPDATE_DETAILS_STATUS.FAIL });
            handleError(`shipping address change error - ${e}`);
        }
    }

    /**
     * @see https://stripe.com/docs/js/payment_request/events/on_shipping_option_change
     */
    async #onShippingOptionChange({ shippingOption, updateWith }) {
        try {
            await this.#submit({ shippingOptionId: shippingOption ? shippingOption.id : null });
            // At this point, the product data should be valid and ready for finalization.
            const hasProductErrors = doesObjHaveErrors(this.#getProductData());
            const hasShippingErrors = doesObjHaveErrors(this.purchaseData.shipto_address);

            // If there are errors in the product data, dismiss the payment request UI.
            // Errors in the shipping address is ok, because it will display an error message for it.
            const updateStatus =
                hasProductErrors && !hasShippingErrors ? UPDATE_DETAILS_STATUS.FAIL : UPDATE_DETAILS_STATUS.SUCCESS;

            updateWith({
                status: updateStatus,
                total: this.#getTotalPaymentItem(),
                displayItems: this.#getPaymentItems(),
                shippingOptions: this.#getShippingOptions(),
            });
            if (updateStatus === UPDATE_DETAILS_STATUS.FAIL) {
                // the FAIL status dismisses the UI.
                // The user might be able to fix the problem with the standard UI, which has more specific messaging.
                AlerterView.error('Unable to find a valid shipping option');
            }
        } catch (e) {
            updateWith({ status: UPDATE_DETAILS_STATUS.FAIL });
            handleError(`shipping option change error - ${e}`);
        }
    }

    /**
     * Fired after the user confirms the payment in the system dialog.
     * This will finalize the purchase and redirect the user to the 'thank you' page if everything works.
     *
     * @see https://stripe.com/docs/js/payment_request/events/on_token
     * @see https://stripe.com/docs/js/appendix/payment_response
     */
    async #onToken(paymentResponse) {
        const { token, shippingAddress, shippingOption, payerPhone, payerEmail, payerName } = paymentResponse;
        // Use setTimeout without a delay so that the analytics code request doesn't block.
        setTimeout(() => trackPaymentRequestAuthorized(this.availableWalletName, this.isExpressCheckout));

        try {
            if (this.isExpressCheckout) {
                await this.#submit({
                    payerEmail,
                    payerName,
                    payerPhone,
                    shippingAddress,
                    shippingPartnerCode: shippingOption ? shippingOption.id : null,
                    shouldFinalize: true,
                    stripeToken: token.id,
                });
            } else {
                await this.#submit({
                    shouldFinalize: true,
                    stripeToken: token.id,
                });
            }

            const successUrl = this.purchaseData.meta && this.purchaseData.meta.success_url;
            if (successUrl) {
                paymentResponse.complete('success');
                trackPaymentComplete(this.availableWalletName, this.#getTotal(), this.expressCheckoutOrigin);
                window.location.href = successUrl;
            } else {
                const finalizeErrors = this.purchaseData.meta.should_finalize_purchase.errors || [];
                const shiptoResponse = this.purchaseData.shipto_address;
                if (doesObjHaveErrors(shiptoResponse)) {
                    paymentResponse.complete('invalid_shipping_address');
                    // In express checkout, we are unable to validate the shipto address before finalization.
                    // This means that they might reach a new shipping validation error at this point.
                    // One way this can happen is if they enter a P.O. box.
                    Object.entries(shiptoResponse).some(([key, val]) => {
                        if (val && val.errors && val.errors.length > 0) {
                            const humanReadableKey = key.replaceAll('_', ' ');
                            AlerterView.error(`${humanReadableKey} invalid: ${val.errors[0].display}`, 0);
                            // only render 1 alert.
                            return true;
                        }
                        return false;
                    });
                } else {
                    paymentResponse.complete('fail');
                    if (finalizeErrors.length > 0) {
                        finalizeErrors.forEach(({ display }) => AlerterView.error(display));
                    } else {
                        AlerterView.error(getGenericPaymentError());
                    }
                }
                trackSubmitError(this.purchaseData, this.availableWalletName, this.isExpressCheckout);
            }
        } catch (e) {
            paymentResponse.complete('fail');
            handleError(`on token error - ${e}`);
        }
    }

    resetPurchaseData() {
        this.purchaseData = this.getPurchaseData();
    }

    /**
     * Submits the given data and updates this.purchaseData with the response.
     *
     * @param {Object} options
     * @param {string|null} options.payerEmail
     * @param {string|null} options.payerName
     * @param {string|null} options.payerPhone
     * @param {Object|null} options.shippingAddress
     * @param {string|null} options.shippingOptionId
     * @param {Object|null} options.shouldFinalize
     * @param {string|null} options.stripeToken
     * @returns {Promise}
     */
    async #submit({
        payerEmail,
        payerName,
        payerPhone,
        shippingAddress = null,
        shippingOptionId = null,
        shouldFinalize = false,
        stripeToken = null,
    }) {
        const account =
            this.isExpressCheckout && (!isAuthenticated() || !this.purchaseData.account)
                ? {
                      email: { value: payerEmail },
                      is_opted_in: { value: false },
                      name: { value: payerName },
                  }
                : this.purchaseData.account;

        const redactedAddressPlaceholder = getRedactedAddressPlaceholder();
        const redactedPhonePlaceholder = getRedactedPhonePlaceholder();

        let shiptoAddress = null;
        if (!shippingAddress) {
            // This is always the case for standard checkout.
            // In express checkout, shipping address is not passed on shipping option change, so use the last one.
            shiptoAddress = this.purchaseData.shipto_address;
        } else {
            // Translate the payment request format to our format.
            shiptoAddress = {
                name: { value: shippingAddress.recipient || payerName || redactedAddressPlaceholder },
                street_address_line_1: {
                    value: shippingAddress.addressLine[0] || redactedAddressPlaceholder,
                },
                street_address_line_2: { value: shippingAddress.addressLine[1] || '' },
                city: { value: shippingAddress.city },
                state: { value: shippingAddress.region },
                postal_code: { value: shippingAddress.postalCode },
                phone_number: { value: shippingAddress.phone || payerPhone || redactedPhonePlaceholder },
                country_code: { value: shippingAddress.country },
            };
            if (!this.wasAddressRedacted) {
                this.wasAddressRedacted = Object.values(shiptoAddress).some(
                    (obj) => obj?.value === redactedAddressPlaceholder,
                );
            }
        }

        const productData = this.#getProductData();
        const newShippingPartnerValue = shippingOptionId || productData.shipping_partner_code?.value;

        let { products } = this.purchaseData;
        if (this.isExpressCheckout) {
            products = [
                {
                    ...productData,
                    shipping_partner_code: { value: newShippingPartnerValue },
                    is_trade_insurance_attached: { value: isTradeInsuranceAvailable(productData) },
                },
            ];
        }

        const purchaseRequest = {
            account,
            billto_address: {},
            shipto_address: shiptoAddress,
            products,
            meta: {
                ...this.purchaseData.meta,
                should_finalize_purchase: { value: shouldFinalize },
            },
            purchase_details: {
                ...this.purchaseData.purchase_details,
                has_agreed_to_tosapp: { value: shouldFinalize },
                was_address_redacted: { value: this.wasAddressRedacted },
            },
            payment_method: {
                payment_processor: { value: getPaymentProcessorCodes().STRIPE },
                is_token_deferred: { value: !shouldFinalize },
                wallet_name: { value: this.availableWalletName },
                stripe_token: { value: stripeToken },
            },
        };

        try {
            const data = await postJSON(this.purchaseUrl, purchaseRequest);
            if (shouldFinalize) {
                reportTotalMismatchDuringFinalization(this.purchaseData, data, this.availableWalletName);
            }
            this.purchaseData = data;
        } catch (error) {
            handleAsyncError(error);
        }
    }

    trackAvailability() {
        trackPaymentRequestAvailable(this.availableWalletName, this.isExpressCheckout);
    }
}

export default PaymentRequestCheckout;
